import { LitElement, css, html, PropertyValues } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

@customElement('tfp-modal')
export class TFPModal extends LitElement {
  @property({ type: Boolean, reflect: true }) open = false;
  @property({ type: String }) mode: 'center' | 'sheet' = 'center';
  @property({ type: Boolean, reflect: true }) closing = false;

  @query('dialog') dialog!: HTMLDialogElement;

  updated(changedProperties: PropertyValues) {
    if (changedProperties.has('open')) {
      if (this.open) {
        if (!this.dialog.open) {
          this.closing = false;
          this.dialog.showModal();
        }
      } else {
        if (this.dialog.open && !this.closing) {
          this.close();
        }
      }
    }
  }

  show() {
    this.open = true;
    this.closing = false;
    this.dialog.showModal();
  }

  close() {
    if (this.closing) return;
    this.closing = true;

    // Wait for animation to finish
    const onAnimationEnd = () => {
      this.open = false;
      this.dialog.close();
      this.dispatchEvent(new CustomEvent('tfp-closed'));
      // Delay resetting closing state to ensure any 'close' event from dialog.close()
      // is ignored by handleClose
      queueMicrotask(() => {
        this.closing = false;
      });
    };

    this.dialog.addEventListener('animationend', onAnimationEnd, { once: true });
  }

  private handleClose = (e: Event) => {
    // If the dialog is closed by the browser (e.g. Esc key), we need to prevent it
    // and run our closing animation first, unless we are already closing or closed.
    if (!this.closing) {
      if (!this.open) {
        this.dispatchEvent(new CustomEvent('closed'));
        return;
      }
      e.preventDefault();
      this.close();
    } else {
      // If we are already closing, let the event propagate but ensure state is sync
      this.open = false;
      this.dispatchEvent(new CustomEvent('closed'));
    }
  }

  private handleCancel = (e: Event) => {
    e.preventDefault();
    this.close();
  }

  private handleBackdropClick = (e: MouseEvent) => {
    if (e.target !== this.dialog) {
      return;
    }
    const rect = this.dialog.getBoundingClientRect();
    const isInDialog = (rect.top <= e.clientY && e.clientY <= rect.top + rect.height
      && rect.left <= e.clientX && e.clientX <= rect.left + rect.width);
    if (!isInDialog) {
      this.close();
    }
  }

  render() {
    return html`
      <dialog
        part="dialog"
        class=${classMap({ 'sentry-unmask': true, 'sentry-unblock': true, [this.mode]: true, closing: this.closing })}
        data-sentry-unmask
        data-sentry-unblock
        @close=${this.handleClose}
        @cancel=${this.handleCancel}
        @click=${this.handleBackdropClick}
      >
        <slot></slot>
      </dialog>
    `;
  }

  static styles = css`
    :host {
      display: contents;
    }

    :host([open]) {
      display: block;
      position: fixed;
      inset: 0;
      pointer-events: none; /* Let pointer events pass directly through to dialog and iframe */
      z-index: 9999;
    }

    /*
    :host {
      display: block;
      position: absolute;
      width: 0;
      height: 0;
      overflow: visible;
    }
    */

    dialog {
      pointer-events: auto; /* Re-enable pointer events on the dialog container and slotted children */
      border: none;
      padding: 0;
      background: transparent;
      color: inherit;
      max-width: 100%;
      max-height: 100dvh;
    }

    dialog::backdrop {
      background-color: color-mix(in srgb, var(--tfp-md-color-scrim), transparent 50%);
      backdrop-filter: blur(1px);
      transition: opacity var(--tfp-md-motion-duration-short-2) var(--tfp-md-motion-easing-standard), backdrop-filter var(--tfp-md-motion-duration-short-2) var(--tfp-md-motion-easing-standard);
    }

    dialog.closing::backdrop {
      opacity: 0;
      backdrop-filter: blur(0px);
    }

    /* Center Mode */
    dialog.center {
      border-radius: var(--tfp-md-shape-corner-extra-large);
      padding: 15px;
      background-color: var(--tfp-md-color-background);
      box-shadow: var(--tfp-md-elevation-level-3);
    }

    /* Sheet Mode */
    dialog.sheet {
      margin: 0;
      margin-top: auto;
      width: 100%;
      max-height: 90dvh;
      border-top-left-radius: var(--tfp-md-shape-corner-large);
      border-top-right-radius: var(--tfp-md-shape-corner-large);
      background-color: var(--tfp-md-color-background);
      box-shadow: var(--tfp-md-elevation-level-1);
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
    }

    /* Animations */
    dialog[open]:not(.closing) {
      animation: fade-in var(--tfp-md-motion-duration-medium-2) var(--tfp-md-motion-easing-emphasized);
    }

    dialog.sheet[open]:not(.closing) {
      animation: slide-up var(--tfp-md-motion-duration-medium-2) var(--tfp-md-motion-easing-emphasized);
    }

    dialog.closing {
      animation: fade-out var(--tfp-md-motion-duration-short-2) var(--tfp-md-motion-easing-emphasized-accelerate) forwards;
    }

    dialog.sheet.closing {
      animation: slide-down var(--tfp-md-motion-duration-short-2) var(--tfp-md-motion-easing-emphasized-accelerate) forwards;
    }

    @keyframes fade-in {
      from { opacity: 0; transform: scale(0.9); }
      to { opacity: 1; transform: scale(1); }
    }

    @keyframes fade-out {
      from { opacity: 1; transform: scale(1); }
      to { opacity: 0; transform: scale(0.9); }
    }

    @keyframes slide-up {
      from { transform: translateY(100%); }
      to { transform: translateY(0); }
    }

    @keyframes slide-down {
      from { transform: translateY(0); }
      to { transform: translateY(100%); }
    }
  `;
}

declare global {
  interface HTMLElementTagNameMap {
    'tfp-modal': TFPModal;
  }
}
